import { useCallback } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';

import { webcamStyle } from './Camera';

import { WhiteArrowBackIcon } from '~assets/icons';
import { FILE_LOADING_STATE } from '~hooks/upload-file';
import { CAMERA_STEP, useCameraContext } from '~providers/CameraProvider';

export const ImagePreview: React.FC<{
  isVisible?: boolean;
}> = ({ isVisible = false }) => {
  const {
    selectedImageSrc,
    handleSetSelectedImageSrc,
    handleSetCameraStep,
    selectedImageFile,
    onUploadFile,
    loadingState,

    isCameraOpen,
    caption,
    description,
  } = useCameraContext();

  const onRetake = useCallback(() => {
    handleSetSelectedImageSrc(null);
    handleSetCameraStep(CAMERA_STEP.CAPTURE);
  }, [handleSetCameraStep, handleSetSelectedImageSrc]);

  const onBack = useCallback(() => {
    handleSetSelectedImageSrc(null);
    handleSetCameraStep(CAMERA_STEP.UNSET);
  }, [handleSetCameraStep, handleSetSelectedImageSrc]);

  if (!selectedImageSrc || !selectedImageFile) return null;

  return (
    <Box
      sx={{
        ...webcamStyle(false),
        visibility: isVisible ? 'visible' : 'hidden',
        alignItems: 'center',
        height: '100%',
        width: '100%',
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        width="100%"
        py={2.5}
        color="white"
        position={'absolute'}
        top={0}
      >
        {!isCameraOpen && (
          <IconButton
            size="medium"
            sx={{
              position: 'absolute',
              left: 0,
            }}
            onClick={onBack}
          >
            <WhiteArrowBackIcon />
          </IconButton>
        )}
        <Stack
          alignItems={'center'}
          width={'100%'}
          sx={{
            paddingLeft: '64px',
            paddingRight: '64px',
            textAlign: 'center',
          }}
        >
          <Typography fontSize="0.875rem" fontWeight={600}>
            {caption}
          </Typography>
          <Typography fontSize="0.75rem" fontWeight={400}>
            {description}
          </Typography>
        </Stack>
      </Box>
      <Box flex={1} alignContent="center">
        <img
          alt="preview image"
          src={selectedImageSrc}
          style={{ objectFit: 'contain', width: '100%', height: '100%' }}
        />
      </Box>

      {/* Buttons Section */}
      {isCameraOpen && (
        <Stack
          width={'100%'}
          px={2.5}
          py={2}
          gap={2}
          flexDirection={'row'}
          alignItems="center"
          position={'absolute'}
          bottom={0}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={onRetake}
            sx={{ borderRadius: '100px', bgcolor: '#374151' }}
          >
            Retake
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={loadingState === FILE_LOADING_STATE.LOADING}
            onClick={() => onUploadFile(selectedImageFile)}
            sx={{
              borderRadius: '100px',
              bgcolor: 'text.highlight',
              ':disabled': {
                bgcolor: 'text.secondary',
              },
            }}
          >
            Use photo
          </Button>
        </Stack>
      )}
    </Box>
  );
};
